import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SynonymError, SynonymRule, SynonymSaveResponse, SynonymWarning } from '../../../lib/api/searchDictionary';
import { searchDictionaryQuery } from '../queries';
import { readApiError } from '../utils/apiError';
import { formatTerms, parseTerms, sameTerms } from '../utils/terms';
import { useDeleteSynonymMutation, useSaveSynonymMutation } from './useSynonymMutations';
import { useDebouncedValue } from './useDebouncedValue';

type UseSynonymEditorParams = {
  /** 고칠 규칙. null 이면 새 규칙 */
  rule: SynonymRule | null;
  /** 저장(되돌리기 포함)이 끝난 뒤 — 새 규칙이면 응답의 rule 로 상세 이동 등 */
  onSaved?: (response: SynonymSaveResponse) => void;
  /** 등록 화면에서 저장하고 상세로 넘어온 경우 그 결과 — 건수 전/후와 「되돌리기」(=삭제)를 이어서 보여 준다 */
  initialLastSave?: SynonymSaveResponse | null;
};

export type SynonymEditor = ReturnType<typeof useSynonymEditor>;

/**
 * 동의어 한 줄 편집 — 빠른 편집 모달과 규칙 상세 페이지가 같이 쓴다 (AMZUWEB-694 · S2 · S3-D).
 *
 * 흐름: 입력 → (멈추면) 서버 검증 → 오류는 저장 차단, 경고는 「경고 N건 확인하고 저장」 →
 * 저장 즉시 고객 검색에 반영 → 건수 전/후 표시 + 바로 되돌리기.
 *
 * 🔴 `previousTerms` 는 **편집을 시작할 때 본 토큰**이다. 저장 사이 다른 사람이 같은 줄을 바꿨으면 서버가 409 와
 *    지금 값을 돌려준다 — 덮어쓰지 않고 「새로 불러오기」 를 안내한다.
 */
export const useSynonymEditor = (params: UseSynonymEditorParams) => {
  const { rule, onSaved, initialLastSave } = params;
  const ruleTerms = useMemo(() => rule?.terms ?? [], [rule]);

  const [baseTerms, setBaseTerms] = useState<string[]>(ruleTerms);
  const [text, setText] = useState(formatTerms(ruleTerms));
  const [serverErrors, setServerErrors] = useState<SynonymError[]>([]);
  const [serverWarnings, setServerWarnings] = useState<SynonymWarning[] | null>(null);
  const [conflict, setConflict] = useState<string[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [lastSave, setLastSave] = useState<{ response: SynonymSaveResponse; before: string[] | null } | null>(() =>
    initialLastSave ? { response: initialLastSave, before: null } : null,
  );

  const terms = useMemo(() => parseTerms(text), [text]);
  const isDirty = !sameTerms(terms, baseTerms);
  const isEditable = !rule?.isMapping;

  // 규칙을 다시 읽어 왔는데 아직 손대지 않았다면 새 값으로 맞춘다(저장·되돌리기 뒤 목록 갱신 포함)
  useEffect(() => {
    if (!isDirty) {
      setBaseTerms(ruleTerms);
      setText(formatTerms(ruleTerms));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleTerms]);

  const debouncedTerms = useDebouncedValue(terms);
  const validation = useQuery({
    ...searchDictionaryQuery.validation({ terms: debouncedTerms, ruleId: rule?.id }),
    enabled: isEditable && debouncedTerms.length > 0,
    keepPreviousData: true,
    staleTime: 30_000,
  });

  // 지금 입력에 대한 검증만 믿는다 — 타이핑 중엔 이전 입력의 결과(keepPreviousData)가 남아 있어
  // 그걸로 저장 버튼을 열거나 confirmWarnings 를 정하면 안 된다
  const isValidationCurrent = Boolean(
    validation.data && !validation.isFetching && !validation.isPreviousData && sameTerms(debouncedTerms, terms),
  );
  const isChecking = isEditable && terms.length > 0 && !isValidationCurrent;
  const currentValidation = isValidationCurrent ? validation.data : undefined;
  const errors = serverErrors.length ? serverErrors : currentValidation?.errors ?? [];
  const warnings = serverWarnings ?? currentValidation?.warnings ?? [];

  const saveMutation = useSaveSynonymMutation();
  const deleteMutation = useDeleteSynonymMutation();

  const resetFeedback = (): void => {
    setServerErrors([]);
    setServerWarnings(null);
    setMessage(null);
  };

  const changeText = (next: string): void => {
    setText(next);
    resetFeedback();
    setLastSave(null);
  };

  const save = async (): Promise<void> => {
    if (!terms.length || errors.length || conflict || isChecking) return;
    resetFeedback();
    try {
      const response = await saveMutation.mutateAsync({
        ruleId: rule?.id,
        terms,
        previousTerms: baseTerms,
        // 경고가 보이는 상태에서 누른 저장은 「확인하고 저장」이다
        confirmWarnings: warnings.length > 0,
      });
      setLastSave({ response, before: rule ? baseTerms : null });
      if (response.rule) {
        setBaseTerms(response.rule.terms);
        setText(formatTerms(response.rule.terms));
      }
      // 이어서 이동하는 쪽(상세)은 usePrompt 의 allowNextNavigation 으로 확인창을 건너뛴다
      onSaved?.(response);
    } catch (error) {
      const parsed = readApiError(error, '저장하지 못했습니다. 잠시 뒤 다시 시도해 주세요.');
      if (parsed.status === 409 && parsed.current) {
        // 그 사이 다른 사람이 바꿨다 — 덮어쓰지 않고 새로 불러오기를 안내한다
        setConflict(parsed.current);
        setMessage(parsed.message);
      } else if (parsed.status === 409) {
        // 다른 사람이 같은 줄을 저장하는 중(잠금) — 입력은 그대로 두고 잠시 뒤 다시 저장하게 한다
        setMessage(parsed.message);
      } else if (parsed.status === 422) {
        // 검증 뒤에 다른 줄이 바뀌어 경고가 새로 생긴 경우 — 보여 주고 한 번 더 받는다
        setServerWarnings(parsed.warnings);
      } else {
        setServerErrors(parsed.errors);
        setMessage(parsed.message);
      }
    }
  };

  /**
   * 방금 저장한 것을 바로 되돌린다. 새 규칙이면 지우고, 수정이면 이전 토큰으로 다시 저장한다.
   * 이력의 「이 시점으로」 와 같은 결과지만, 이력은 큐를 거쳐 쌓여 저장 직후에는 아직 없을 수 있다.
   */
  const undo = async (): Promise<void> => {
    if (!lastSave?.response.rule) return;
    const saved = lastSave.response.rule;
    resetFeedback();
    try {
      const response =
        lastSave.before === null
          ? await deleteMutation.mutateAsync({ ruleId: saved.id, previousTerms: saved.terms })
          : await saveMutation.mutateAsync({
              ruleId: saved.id,
              terms: lastSave.before,
              previousTerms: saved.terms,
              confirmWarnings: true,
            });
      setLastSave(null);
      onSaved?.(response);
      if (lastSave.before) {
        setBaseTerms(lastSave.before);
        setText(formatTerms(lastSave.before));
      }
    } catch (error) {
      setMessage(readApiError(error, '되돌리지 못했습니다. 변경 이력에서 다시 시도해 주세요.').message);
    }
  };

  /** 409 뒤 — 지금 저장된 값으로 다시 시작한다 */
  const reload = (): void => {
    const current = conflict ?? ruleTerms;
    setConflict(null);
    setBaseTerms(current);
    setText(formatTerms(current));
    resetFeedback();
    setLastSave(null);
  };

  return {
    text,
    setText: changeText,
    terms,
    baseTerms,
    isDirty,
    isEditable,
    errors,
    warnings,
    isValidating: validation.isFetching,
    isChecking,
    conflict,
    message,
    lastSave: lastSave?.response ?? null,
    isSaving: saveMutation.isLoading || deleteMutation.isLoading,
    canSave: isEditable && isDirty && terms.length > 0 && errors.length === 0 && !conflict && !isChecking,
    saveLabel:
      isChecking && isDirty ? '확인 중…' : warnings.length > 0 ? `경고 ${warnings.length}건 확인하고 저장` : '저장',
    save,
    undo,
    reload,
  };
};
